import { refeshConfig } from "@/api/fbapi";
import { toast } from "@/hooks/use-toast";
import { navigate } from "wouter/use-browser-location";

/**
 * Extracts OAuth error details from error message or response
 */
export function extractOAuthErrorDetails(error: any): {
  code?: number;
  subcode?: number;
  message?: string;
} {
  const errorMessage = error?.message || error?.response?.message || "";
  const errorString = String(errorMessage);

  // Pattern to match Facebook OAuth errors
  // Example: "OAuthException: Error validating access token: Session has expired... (code 190, subcode 463)"
  const oauthPattern = /OAuthException.*?\(code\s+(\d+)(?:,\s*subcode\s+(\d+))?\)/i;
  const match = errorString.match(oauthPattern);

  if (match) {
    return {
      code: parseInt(match[1], 10),
      subcode: match[2] ? parseInt(match[2], 10) : undefined,
      message: errorMessage,
    };
  }

  // Also check for error code in response data
  if (error?.response?.data) {
    const data = error.response.data;
    if (data.code === 190 || data.error?.code === 190) {
      return {
        code: data.code || data.error?.code,
        subcode: data.error_subcode || data.error?.error_subcode,
        message: data.message || data.error?.message || errorMessage,
      };
    }
  }

  return {
    message: errorMessage,
  };
}

/**
 * Checks if an error is a Facebook OAuth expiration error
 * OAuth expiration errors have code 190 and subcode 463
 */
export function isOAuthExpirationError(error: any): boolean {
  const details = extractOAuthErrorDetails(error);
  return details.code === 190 && details.subcode === 463;
}

/**
 * Handles Facebook reconnection flow
 * - Calls refresh config API
 * - Shows success/error toasts
 * - Redirects to /fblogin on success
 */
export async function handleFacebookReconnection(): Promise<void> {
  try {
    const refreshResponse = await refeshConfig();
    if (refreshResponse.success) {
      toast({
        title: "Success",
        description: "Configuration refreshed. Redirecting to Facebook login...",
      });
      setTimeout(() => {
        navigate("/fblogin");
      }, 1000);
    } else {
      toast({
        title: "Error",
        description:
          refreshResponse.message || "Failed to refresh configuration",
        variant: "destructive",
      });
    }
  } catch (error: any) {
    toast({
      title: "Error",
      description: error?.message || "An unexpected error occurred",
      variant: "destructive",
    });
  }
}

